import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  BarChart3, TrendingUp, ThumbsUp, MessageCircle, Share2, Eye,
  Users, RefreshCw, Loader2, MousePointerClick,
  ArrowUpRight, ArrowDownRight, AlertTriangle, CheckCircle2, Clock, Info, LineChart
} from 'lucide-react';


const apiHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('hw_token')}`,
  'Content-Type': 'application/json'
});

const formatNumber = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString('it-IT');
};

const StatusIndicator = ({ status }) => {
  if (status === 'ok') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
  if (status === 'not_on_facebook') return <Clock className="h-3.5 w-3.5 text-amber-500" title="Non ancora pubblicato su Facebook" />;
  if (status === 'token_missing') return <AlertTriangle className="h-3.5 w-3.5 text-rose-500" title="Token della pagina mancante" />;
  if (status === 'fetch_error' || status === 'partial_error') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Errore nel recupero metriche" />;
  return null;
};

export default function AnalyticsTab({ pages }) {
  const [pageInsights, setPageInsights] = useState([]);
  const [postInsights, setPostInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [loading, setLoading] = useState({ pages: false, posts: false });
  const [selectedPage, setSelectedPage] = useState('');
  const [expandedPost, setExpandedPost] = useState(null);
  const [comments, setComments] = useState({});
  const [view, setView] = useState('pages');

  const fetchPageInsights = useCallback(async () => {
    setLoading(prev => ({ ...prev, pages: true }));
    try {
      const r = await fetch(`${API}/api/social/analytics/pages`, { headers: apiHeaders() });
      if (r.ok) setPageInsights(await r.json());
    } catch (e) { console.error(e); }
    setLoading(prev => ({ ...prev, pages: false }));
  }, []);

  const fetchPostInsights = useCallback(async () => {
    setLoading(prev => ({ ...prev, posts: true }));
    try {
      let url = `${API}/api/social/analytics/posts?limit=30`;
      if (selectedPage) url += `&page_id=${selectedPage}`;
      const r = await fetch(url, { headers: apiHeaders() });
      if (r.ok) setPostInsights(await r.json());
    } catch (e) { console.error(e); }
    setLoading(prev => ({ ...prev, posts: false }));
  }, [selectedPage]);

  const fetchTrends = useCallback(async () => {
    setLoadingTrends(true);
    try {
      const r = await fetch(`${API}/api/social/analytics/pages-trends`, { headers: apiHeaders() });
      if (r.ok) setTrends(await r.json());
    } catch (e) { console.error(e); }
    setLoadingTrends(false);
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/social/analytics/summary`, { headers: apiHeaders() });
      if (r.ok) setSummary(await r.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchComments = async (postId) => {
    if (comments[postId]) {
      setExpandedPost(expandedPost === postId ? null : postId);
      return;
    }
    try {
      const r = await fetch(`${API}/api/social/analytics/comments/${postId}`, { headers: apiHeaders() });
      if (r.ok) {
        const data = await r.json();
        setComments(prev => ({ ...prev, [postId]: data.comments }));
        setExpandedPost(postId);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    if (view === 'pages') fetchPageInsights();
    if (view === 'posts') fetchPostInsights();
  }, [view, fetchPageInsights, fetchPostInsights]);

  const summaryCards = summary ? [
    { label: 'Pagine Collegate', value: summary.connected_pages, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Post Totali', value: summary.published_posts_tracked, icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50',
      sub: summary.posts_with_insights > 0 ? `${summary.posts_with_insights} con metriche FB` : null },
    { label: 'Interazioni Totali', value: formatNumber(summary.total_interactions), icon: ThumbsUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Impressioni', value: formatNumber(summary.total_impressions), icon: Eye, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Engagement Medio', value: summary.avg_engagement_rate + '%', icon: TrendingUp, color: 'text-rose-600', bg: 'bg-rose-50' },
  ] : [];

  const hasNoFbData = summary && summary.posts_with_insights === 0 && summary.published_posts_tracked > 0;

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {summaryCards.map(s => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg ${s.bg}`}>
                    <s.icon className={`h-4 w-4 ${s.color}`} />
                  </div>
                  <div>
                    <div className={`text-lg font-bold ${s.color}`} data-testid={`summary-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>{s.value}</div>
                    <div className="text-[10px] text-slate-500 leading-tight">{s.label}</div>
                    {s.sub && <div className="text-[9px] text-slate-400">{s.sub}</div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info banner when no FB data */}
      {hasNoFbData && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="no-fb-data-banner">
          <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-700">
            <strong>Metriche Facebook non disponibili.</strong> I post approvati/pubblicati sono visibili, ma le metriche di engagement (like, commenti, condivisioni) verranno mostrate solo dopo la pubblicazione effettiva su Facebook e il recupero dei dati. Controlla che i token delle pagine siano validi nel tab "Pagine".
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={view === 'pages' ? 'default' : 'outline'}
          onClick={() => setView('pages')}
          data-testid="analytics-view-pages"
        >
          <Users className="h-3.5 w-3.5 mr-1.5" /> Pagine
        </Button>
        <Button
          size="sm"
          variant={view === 'posts' ? 'default' : 'outline'}
          onClick={() => setView('posts')}
          data-testid="analytics-view-posts"
        >
          <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Post
        </Button>
        <Button
          size="sm"
          variant={view === 'trends' ? 'default' : 'outline'}
          onClick={() => { setView('trends'); if (trends.length === 0) fetchTrends(); }}
          data-testid="analytics-view-trends"
        >
          <LineChart className="h-3.5 w-3.5 mr-1.5" /> Trends (M/T/A)
        </Button>
        <div className="flex-1" />
        <Button
          size="sm" variant="ghost"
          onClick={() => {
            if (view === 'pages') fetchPageInsights();
            else if (view === 'posts') fetchPostInsights();
            else if (view === 'trends') fetchTrends();
            fetchSummary();
          }}
          disabled={loading.pages || loading.posts || loadingTrends}
          data-testid="analytics-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${(loading.pages || loading.posts || loadingTrends) ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* PAGES VIEW */}
      {view === 'pages' && (
        <div className="space-y-2">
          {loading.pages ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-slate-500">Recupero dati da Facebook...</span>
            </div>
          ) : (
            <>
              <div className="grid gap-2">
                {pageInsights.map(page => (
                  <Card key={page.id} className="border shadow-sm hover:shadow transition-shadow">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {page.name?.replace('Holiday World ', '').charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{page.name}</div>
                            <div className="text-[10px] text-slate-400">{page.category || page.region}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-center flex-shrink-0">
                          <div>
                            <div className="text-sm font-bold text-blue-700">{formatNumber(page.fan_count)}</div>
                            <div className="text-[9px] text-slate-400">Fan</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-indigo-700">{formatNumber(page.followers_count)}</div>
                            <div className="text-[9px] text-slate-400">Follower</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-amber-600">{formatNumber(page.page_views_28d)}</div>
                            <div className="text-[9px] text-slate-400">Visite 28g</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-emerald-600">{formatNumber(page.post_engagements_28d)}</div>
                            <div className="text-[9px] text-slate-400">Interazioni 28g</div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-rose-600">{formatNumber(page.talking_about_count)}</div>
                            <div className="text-[9px] text-slate-400">Parlano di noi</div>
                          </div>
                        </div>
                      </div>
                      {page.error && (
                        <div className="text-[10px] text-rose-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Errore: {page.error}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              {pageInsights.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nessuna pagina collegata con token valido</p>
                  <p className="text-xs mt-1">Collega le pagine Facebook dal tab "Pagine"</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* POSTS VIEW */}
      {view === 'posts' && (
        <div className="space-y-3">
          {/* Filter by page */}
          <select
            className="border rounded-md px-3 py-1.5 text-sm bg-white w-64"
            value={selectedPage}
            onChange={e => setSelectedPage(e.target.value)}
            data-testid="analytics-filter-page"
          >
            <option value="">Tutte le pagine</option>
            {pages.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {loading.posts ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-slate-500">Recupero metriche post...</span>
            </div>
          ) : (
            <>
              {/* Posts table */}
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm" data-testid="posts-analytics-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Post</th>
                      <th className="text-center px-2 py-2 font-medium w-12">Stato</th>
                      <th className="text-center px-2 py-2 font-medium w-16">
                        <ThumbsUp className="h-3.5 w-3.5 mx-auto" />
                      </th>
                      <th className="text-center px-2 py-2 font-medium w-16">
                        <MessageCircle className="h-3.5 w-3.5 mx-auto" />
                      </th>
                      <th className="text-center px-2 py-2 font-medium w-16">
                        <Share2 className="h-3.5 w-3.5 mx-auto" />
                      </th>
                      <th className="text-center px-2 py-2 font-medium w-20">
                        <Eye className="h-3.5 w-3.5 mx-auto" />
                      </th>
                      <th className="text-center px-2 py-2 font-medium w-16">
                        <MousePointerClick className="h-3.5 w-3.5 mx-auto" />
                      </th>
                      <th className="text-center px-2 py-2 font-medium w-16">
                        <ArrowUpRight className="h-3.5 w-3.5 mx-auto" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {postInsights.map(post => {
                      const ins = post.insights || {};
                      const notPublished = ins.not_published;
                      const hasError = ins.error;
                      const isClickable = post.fb_post_id;

                      return (
                        <React.Fragment key={post.id}>
                          <tr
                            className={`border-t hover:bg-slate-50 ${isClickable ? 'cursor-pointer' : ''} ${notPublished ? 'bg-amber-50/40' : ''}`}
                            onClick={() => isClickable && fetchComments(post.id)}
                          >
                            <td className="px-3 py-2">
                              <div className="font-medium text-xs truncate max-w-xs">{post.page_name}</div>
                              <div className="text-[10px] text-slate-400 truncate max-w-xs">
                                {(post.edited_text || post.original_text || '').substring(0, 60)}...
                              </div>
                              <div className="text-[9px] text-slate-300">{post.scheduled_date}</div>
                            </td>
                            <td className="text-center px-2 py-2">
                              <div className="flex flex-col items-center gap-0.5">
                                <StatusIndicator status={post.analytics_status} />
                                <Badge className={`text-[8px] px-1 py-0 ${
                                  post.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                                  post.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {post.status === 'published' ? 'Pub' : post.status === 'approved' ? 'Appr' : post.status}
                                </Badge>
                              </div>
                            </td>
                            <td className="text-center px-2 py-2 font-semibold text-blue-600">
                              {notPublished ? <span className="text-slate-300">-</span> : (hasError ? <AlertTriangle className="h-3.5 w-3.5 mx-auto text-rose-400" /> : ins.likes ?? '-')}
                            </td>
                            <td className="text-center px-2 py-2 font-semibold text-amber-600">
                              {notPublished ? <span className="text-slate-300">-</span> : (hasError ? <span className="text-slate-300">-</span> : ins.comments ?? '-')}
                            </td>
                            <td className="text-center px-2 py-2 font-semibold text-green-600">
                              {notPublished ? <span className="text-slate-300">-</span> : (hasError ? <span className="text-slate-300">-</span> : ins.shares ?? '-')}
                            </td>
                            <td className="text-center px-2 py-2 font-semibold text-slate-600">
                              {notPublished ? <span className="text-slate-300">-</span> : (ins.impressions ? formatNumber(ins.impressions) : '-')}
                            </td>
                            <td className="text-center px-2 py-2 font-semibold text-indigo-600">
                              {notPublished ? <span className="text-slate-300">-</span> : (ins.clicks ?? '-')}
                            </td>
                            <td className="text-center px-2 py-2">
                              {notPublished ? (
                                <span className="text-slate-300">-</span>
                              ) : ins.engagement_rate ? (
                                <Badge className={`text-[10px] ${ins.engagement_rate > 5 ? 'bg-emerald-100 text-emerald-700' : ins.engagement_rate > 2 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {ins.engagement_rate}%
                                </Badge>
                              ) : '-'}
                            </td>
                          </tr>
                          {/* Error detail row */}
                          {hasError && !notPublished && (
                            <tr>
                              <td colSpan={8} className="bg-rose-50 px-4 py-1.5">
                                <div className="flex items-center gap-1.5 text-[10px] text-rose-600">
                                  <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                  <span>Errore nel recupero metriche: {ins.error}</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {/* Comments expandable */}
                          {expandedPost === post.id && comments[post.id] && (
                            <tr>
                              <td colSpan={8} className="bg-slate-50 px-4 py-2">
                                <div className="text-xs font-medium text-slate-600 mb-1.5">
                                  Commenti ({comments[post.id].length})
                                </div>
                                {comments[post.id].length === 0 ? (
                                  <div className="text-[11px] text-slate-400 py-2">Nessun commento</div>
                                ) : (
                                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                    {comments[post.id].map(c => (
                                      <div key={c.id} className="bg-white rounded p-2 border text-[11px]">
                                        <div className="flex items-center justify-between">
                                          <span className="font-semibold text-slate-700">{c.from?.name || 'Anonimo'}</span>
                                          <span className="text-slate-400">{new Date(c.created_time).toLocaleDateString('it-IT')}</span>
                                        </div>
                                        <p className="text-slate-600 mt-0.5">{c.message}</p>
                                        {c.like_count > 0 && (
                                          <span className="text-blue-500 text-[10px]">{c.like_count} like</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {postInsights.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nessun post approvato o pubblicato</p>
                  <p className="text-xs mt-1">I post appariranno qui dopo l'approvazione o la pubblicazione su Facebook</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* TRENDS VIEW: aumenti mensili / trimestrali / annuali per pagina */}
      {view === 'trends' && (
        <div className="space-y-3" data-testid="analytics-trends-view">
          {loadingTrends ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-slate-500">Calcolo trend in corso...</span>
            </div>
          ) : (
            <TrendsView trends={trends} onRefresh={fetchTrends} />
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE TRENDS VIEW (aumenti M/T/A per ogni pagina)
// ============================================================================
const METRIC_LABELS = {
  fan_count: { label: 'Fan', color: 'text-blue-700', bg: 'bg-blue-50' },
  followers_count: { label: 'Follower', color: 'text-indigo-700', bg: 'bg-indigo-50' },
  page_views_28d: { label: 'Visite 28g', color: 'text-amber-700', bg: 'bg-amber-50' },
  post_engagements_28d: { label: 'Interazioni 28g', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  talking_about_count: { label: 'Parlano di noi', color: 'text-rose-700', bg: 'bg-rose-50' },
};

const PERIOD_LABELS = {
  month: { label: 'Mese (30g)', short: '30g' },
  quarter: { label: 'Trim. (90g)', short: '90g' },
  year: { label: 'Anno (365g)', short: '365g' },
};

function DeltaBadge({ delta, period }) {
  if (!delta || (delta.delta_absolute === 0 && delta.delta_pct === 0)) {
    return <span className="text-[10px] text-slate-400">—</span>;
  }
  const positive = delta.delta_absolute > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <div className="flex items-center gap-0.5">
      <Icon className={`h-3 w-3 ${positive ? 'text-emerald-600' : 'text-rose-600'}`} />
      <span className={`text-[11px] font-semibold ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>
        {positive ? '+' : ''}{delta.delta_pct}%
      </span>
      <span className="text-[9px] text-slate-400 ml-0.5">{PERIOD_LABELS[period]?.short}</span>
    </div>
  );
}

function TrendsView({ trends, onRefresh }) {
  if (!trends || trends.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <LineChart className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Nessun dato di trend disponibile</p>
        <p className="text-xs mt-1">Apri la vista "Pagine" almeno una volta al giorno per accumulare snapshot storici</p>
      </div>
    );
  }
  // Caso: tutte non disponibili → spiega all'utente
  const allUnavailable = trends.every(t => !t.available);
  if (allUnavailable) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="trends-bootstrapping">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">Trends in fase di accumulo dati</p>
            <p className="text-xs text-amber-700 mt-1">
              Per calcolare il trend mensile/trimestrale/annuale serve almeno uno snapshot storico delle metriche di pagina.
              Gli snapshot vengono salvati automaticamente ogni volta che si apre la vista "Pagine".
              Tornerai a vedere i delta dopo i primi 30 giorni di utilizzo.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="trends-list">
      {trends.map(t => (
        <Card key={t.page_id} data-testid={`trends-page-${t.page_id}`}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2 pb-2 border-b">
              <div>
                <div className="text-sm font-semibold text-slate-800">{t.page_name}</div>
                {t.current_snapshot_date && (
                  <div className="text-[10px] text-slate-400">snapshot odierno: {t.current_snapshot_date}</div>
                )}
              </div>
              {!t.available && <span className="text-xs text-slate-400 italic">Dati storici insufficienti</span>}
            </div>

            {!t.available ? (
              <div className="text-xs text-slate-500 py-2">{t.reason}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                {Object.entries(METRIC_LABELS).map(([metric, cfg]) => {
                  const currentVal = t.current?.[metric] ?? 0;
                  return (
                    <div key={metric} className={`rounded-lg ${cfg.bg} p-2 border border-slate-100`}>
                      <div className={`text-[10px] uppercase tracking-wide ${cfg.color} font-semibold`}>
                        {cfg.label}
                      </div>
                      <div className={`text-xl font-bold ${cfg.color} mt-0.5`} data-testid={`trends-${t.page_id}-${metric}-current`}>
                        {formatNumber(currentVal)}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {['month', 'quarter', 'year'].map(period => (
                          <DeltaBadge
                            key={period}
                            delta={t.trends?.[period]?.[metric]}
                            period={period}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
